import { Settings } from "../config";
import {
  FetchStatus,
  LanderCookie,
  FeedProvider,
  EventType,
  ErrorCode,
  System,
  YahooSingleLanderHostname,
} from "../commons/Constants";
import {
  getCookie,
  objectToQueryStringExcludeNull,
} from "../commons/HelperFunctions";
import Logger from "../commons/Logger";
import postEvent, { createBaseLanderEvent } from "../commons/LanderEvent";

const SUCCESS_MESSAGE = "success";

function customerIdToTypeTag(customerId: string): string {
  return customerId.replace(/-/g, "");
}

export function getYahooRSUri({
  rootDomain,
  keywords,
  system,
  yahooGoogleTrafficExperiment,
  customerId,
}: {
  rootDomain: string;
  keywords: string[];
  system: string;
  yahooGoogleTrafficExperiment: boolean;
  customerId?: string;
}) {
  const feedPartner = yahooGoogleTrafficExperiment
    ? Settings.YAHOO_FEED_PARTNER_GOOGLE
    : Settings.YAHOO_FEED_PARTNER;

  let partner = "";
  if (system === System.CASHPARKING) {
    partner = feedPartner.CP;
  } else if (system === System.PARKWEB) {
    partner = feedPartner.PW;
  } else if (system === System.SMARTNAME) {
    partner = feedPartner.SN;
  }

  // if test partner is set, override other config
  if (Settings.YAHOO_FEED_PARTNER.TEST)
    partner = Settings.YAHOO_FEED_PARTNER.TEST;

  let type = "";
  if (system === System.PARKWEB) {
    type = System.PARKWEB;
  } else {
    type = customerIdToTypeTag(customerId);
  }

  const rsParams: any = {
    url: rootDomain,
    Partner: partner,
    maxListings: 3,
    maxWeb: 0,
    maxRT: 6,
    maxRTL: 6,
    maxPC: 6,
    urlLang: "en",
    type: type,
    affilData: {
      ip: getCookie(LanderCookie.CLIENT_IP),
      xfip: getCookie(LanderCookie.CLIENT_IP),
      ua: navigator.userAgent,
      ur: window.location.href.replace(window.location.search, ""), // Strip query params
    },
    serveUrl: `${window.location.protocol}//${rootDomain}`,
  };
  if (keywords) rsParams.seedTerm = keywords.join(" ");

  return `?${objectToQueryStringExcludeNull(rsParams)}`;
}

export function getYahooAdsUri({
  qs,
  rootDomain,
  searchQuery,
  system,
  customerId,
}: {
  qs: string;
  rootDomain: string;
  searchQuery: string;
  system?: string | undefined;
  customerId?: string;
}) {
  // Get url params
  const newURL = new URLSearchParams(window.location.search);
  // Ensure we don't have duplicate url params
  newURL.delete("qs");
  newURL.delete("q");
  newURL.delete("serveURL");
  newURL.delete("yahoo");
  newURL.delete("domain");

  if (searchQuery) newURL.append("q", searchQuery);
  newURL.append("yahoo", "1");
  newURL.append("serveURL", `${window.location.protocol}//${rootDomain}`);

  // System and domain are important here since this uri will take the user to
  // the single lander hostname and show the yahoo ads.
  // The yahoo single lander will need these params to show the correct lander experience.
  if (system) newURL.append("system", system);
  newURL.append("domain", rootDomain);

  let type = "";
  if (system === System.PARKWEB) {
    type = System.PARKWEB;
  } else {
    type = customerIdToTypeTag(customerId);
  }
  newURL.append("type", type);

  const url = new URL(window.location.href);
  url.search = newURL.toString();
  url.search += `&${qs}`;

  url.hostname = YahooSingleLanderHostname;

  return url.toString();
}

export async function yahooXmlFetch(urlParams: string) {
  try {
    const response = await fetch(`${Settings.YAHOO_FEED_PATH}${urlParams}`, {
      method: "GET",
      credentials: "omit",
    });

    if (response.status !== 200)
      throw new Error(`got ${response.status} from yahoo`);

    const txt = await response.text();
    const xml = new DOMParser().parseFromString(txt, "text/xml");
    if (xml.querySelector("parsererror"))
      throw new Error("xml failed to parse");

    // Check for errors from Yahoo Feed
    Array.from(xml.getElementsByTagName("StatusItem")).forEach((statusItem) => {
      const status = parseInt(statusItem.getAttribute("code"), 10);
      if (status >= 400)
        throw new Error(
          `${status} response from yahoo feed: ${statusItem.textContent}`
        );
    });

    return {
      fetchStatus: FetchStatus.SUCCESS,
      xmlResult: xml,
      message: SUCCESS_MESSAGE,
    };
  } catch (err) {
    Logger.error(err);
    return {
      fetchStatus: FetchStatus.FAILURE,
      message: err.message,
    };
  }
}

export function sendYahooEvent(
  message: string,
  isAdClicked: boolean,
  landerConfig: any
) {
  const yahooEvent = createBaseLanderEvent(null, landerConfig);
  yahooEvent.feedProvider = FeedProvider.YAHOO;
  yahooEvent.yahooApiMessage = message;

  if (message !== SUCCESS_MESSAGE) {
    yahooEvent.eventType = EventType.ERROR;
    yahooEvent.errorCode = isAdClicked
      ? ErrorCode.ERROR_YAHOO_NO_ADS
      : ErrorCode.ERROR_YAHOO_NO_RS;
  } else {
    yahooEvent.eventType = isAdClicked
      ? EventType.YAHOO_AD_CLICKED
      : EventType.YAHOO_VISIT;
  }

  postEvent(Settings.EVENT_PUBLISH_API, yahooEvent);
}
